import React from 'react'
import { range } from 'lodash'
import { ChevronLeftSmallIcon, ChevronRightSmallIcon } from '@hub/icon'
import { Box } from '@hub/box'
import { Link } from '@hub/link'

export interface ILinkPaginationProps {
  currentPage: number
  totalPages: number
  makePageLink: (page: number) => string
  onLinkClick?: (page: number) => void
}

const buttonStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  width: 'size-8',
  height: 'size-8',
}

export const generatePages = (
  currentPage: number,
  totalPages: number
): number[] => {
  const maxPageButtons = 5
  // checks if current page is out of range
  let page = currentPage
  if (currentPage < 0) {
    page = 0
  } else if (currentPage > totalPages) {
    page = totalPages - 1
  }

  let startPage = 0
  let endPage = totalPages

  if (totalPages >= maxPageButtons) {
    const medianPageDivisor = 2
    const numberOfPagesBeforeCurrentPage = Math.floor(
      maxPageButtons / medianPageDivisor
    )
    const numberOfPagesAfterCurrentPage = Math.ceil(
      maxPageButtons / medianPageDivisor
    )
    if (page <= numberOfPagesBeforeCurrentPage) {
      startPage = 0
      endPage = maxPageButtons
    } else if (page + numberOfPagesAfterCurrentPage >= totalPages) {
      startPage = totalPages - maxPageButtons
      endPage = totalPages
    } else {
      startPage = page - numberOfPagesBeforeCurrentPage
      endPage = page + numberOfPagesAfterCurrentPage
    }
  }

  return range(startPage, endPage)
}

export const LinkPagination: React.FunctionComponent<
  React.PropsWithChildren<ILinkPaginationProps>
> = ({ currentPage, totalPages, makePageLink, onLinkClick }) => {
  const onLinkClick_ = onLinkClick || (() => undefined)
  const pages = generatePages(currentPage, totalPages)
  const onFirstPage = currentPage === 0
  const onLastPage = totalPages - 1 === currentPage

  return (
    <Box sx={{ display: 'inline-flex' }}>
      {onFirstPage ? (
        <Box
          sx={{
            ...buttonStyles,
            pointerEvents: 'none',
          }}
        >
          <ChevronLeftSmallIcon sx={{ color: 'iconDisabled' }} />
        </Box>
      ) : (
        <Link
          href={makePageLink(currentPage - 1)}
          ariaLabel="Go to previous page"
          onClick={() => onLinkClick_(currentPage - 1)}
          sx={{ ...buttonStyles }}
        >
          <ChevronLeftSmallIcon />
        </Link>
      )}
      {pages.map(page => (
        <Link
          key={`go-to-page-${page + 1}`}
          href={makePageLink(page)}
          ariaLabel={`Go to page ${page + 1}`}
          sx={{
            ...buttonStyles,
            color: 'textPrimary',
            border: page === currentPage ? '1px solid' : '0',
            borderColor: page === currentPage ? 'borderPrimary' : 'transparent',
            pointerEvents: page === currentPage ? 'none' : 'auto',
          }}
          onClick={() => onLinkClick_(page)}
        >
          {page + 1}
        </Link>
      ))}
      {onLastPage ? (
        <Box sx={{ ...buttonStyles, pointerEvents: 'none' }}>
          <ChevronRightSmallIcon sx={{ color: 'iconDisabled' }} />
        </Box>
      ) : (
        <Link
          href={makePageLink(currentPage + 1)}
          sx={{ ...buttonStyles }}
          ariaLabel="Go to next page"
          onClick={() => onLinkClick_(currentPage + 1)}
        >
          <ChevronRightSmallIcon />
        </Link>
      )}
    </Box>
  )
}
